<template>
  <div>
    <!-- Password Field -->
    <div class="mb-3">
      <label class="form-label mc-user-modal__required">
        {{ $t('master.user.password') }}
      </label>
      <b-form-group
        id="input-group-2-action"
        class="mc-user-modal__form-password"
        label-for="input-2-action"
      >
        <div class="position-relative">
          <b-form-input
            v-if="showPassword"
            id="input-password-action"
            :value="form.password"
            type="text"
            :placeholder="$t('master.user.password')"
            :class="{
              'is-invalid': $v.form.password.$invalid,
            }"
            autocomplete="off"
            @input="updatePassword"
            @focus="$emit('show-tooltip', { key: 'Password' })"
            @blur="$emit('hide-tooltip', { key: 'Password' })"
          />
          <b-form-input
            v-else
            id="input-password-action"
            :value="form.password"
            type="password"
            :placeholder="$t('master.user.password')"
            :class="{
              'is-invalid': $v.form.password.$invalid,
            }"
            @input="updatePassword"
            @focus="$emit('show-tooltip', { key: 'Password' })"
            @blur="$emit('hide-tooltip', { key: 'Password' })"
          />
          <i
            class="mc-user-modal__password-toggle"
            :class="{
              'icon-outline_eye': showPassword,
              'icon-outline_eye-off-2': !showPassword,
            }"
            @click="$emit('toggle-password')"
          />
        </div>
        <!-- Password Validation Tooltip -->
        <div v-if="showTooltipPassword" class="mc-user-modal__tooltip">
          <ul
            v-for="(val, idx) in passwordValidationsCases"
            :key="idx"
            class="mc-tooltip__bottom list-unstyled my-2"
          >
            <li class="mb-2">
              <i
                :class="`me-2 icon-fill_${$v.form.password[val] ? 'checkmark' : 'close'}-circle ${
                  $v.form.password[val]
                    ? 'mc-user-modal__icon-success'
                    : 'mc-user-modal__icon-danger'
                }`"
              />
              <label :class="$v.form.password[val] ? 'text-success' : 'text-danger'">
                {{ $t(`password.${val}`) }}
              </label>
            </li>
          </ul>
        </div>
      </b-form-group>
    </div>

    <!-- Confirm Password Field -->
    <div class="mb-2">
      <label class="form-label mc-user-modal__required">
        {{ $t('master.user.repeat') }}
      </label>
      <b-form-group
        id="input-group-3-action"
        class="mc-user-modal__form-password"
        label-for="input-3-action"
      >
        <div class="position-relative">
          <b-form-input
            v-if="showRepassword"
            id="input-repassword-action"
            :value="form.repassword"
            type="text"
            :disabled="!form.password && form.repassword.length == 0"
            :placeholder="$t('master.user.repeat')"
            :class="{
              'is-invalid': $v.form.repassword.$error || !isPasswordMatched,
            }"
            @input="updateRepassword"
            @focus="$emit('show-tooltip', { key: 'Repassword' })"
            @blur="$emit('hide-tooltip', { key: 'Repassword' })"
          />
          <b-form-input
            v-else
            id="input-repassword-action"
            :value="form.repassword"
            type="password"
            :disabled="!form.password && form.repassword.length == 0"
            :placeholder="$t('master.user.repeat')"
            :class="{
              'is-invalid': $v.form.repassword.$error || !isPasswordMatched,
            }"
            @input="updateRepassword"
            @focus="$emit('show-tooltip', { key: 'Repassword' })"
            @blur="$emit('hide-tooltip', { key: 'Repassword' })"
          />
          <i
            class="mc-user-modal__password-toggle"
            :class="{
              'icon-outline_eye': showRepassword,
              'icon-outline_eye-off-2': !showRepassword,
            }"
            @click="$emit('toggle-repassword')"
          />
        </div>
        <!-- Password Match Tooltip -->
        <div
          v-if="showTooltipRepassword && form.password"
          class="mc-user-modal__tooltip-repassword"
        >
          <i
            :class="`me-2 icon-fill_${isPasswordMatched ? 'checkmark' : 'close'}-circle ${
              isPasswordMatched ? 'mc-user-modal__icon-success' : 'mc-user-modal__icon-danger'
            }`"
          />
          <label :class="isPasswordMatched ? 'text-success' : 'text-danger'">
            {{ $t(`password.matched`) }}
          </label>
        </div>
      </b-form-group>
    </div>
  </div>
</template>

<script>
import { requiredIf, minLength, maxLength } from 'vuelidate/lib/validators'

// Custom phone number validator
const phoneNumber = (value) => {
  if (!value) return true
  const cleanPhone = value.replace(/\D/g, '')
  const patterns = [/^8[1-9]\d{7,11}$/, /^0[2-9]\d{7,10}$/]
  return patterns.some((pattern) => pattern.test(cleanPhone))
}

export default {
  name: 'PasswordFields',

  props: {
    form: {
      type: Object,
      required: true,
    },
    showPassword: {
      type: Boolean,
      default: false,
    },
    showRepassword: {
      type: Boolean,
      default: false,
    },
    showTooltipPassword: {
      type: Boolean,
      default: false,
    },
    showTooltipRepassword: {
      type: Boolean,
      default: false,
    },
    passwordValidationsCases: {
      type: Array,
      default: () => [],
    },
    isPasswordMatched: {
      type: Boolean,
      default: false,
    },
  },

  validations: {
    form: {
      password: {
        required: requiredIf(function () {
          return this.$store.state.config.mode !== 'development' && this.form.isDriver
        }),
        maxLength: maxLength(50),
        lowerCase: (val) => {
          const lowercaseRegex = /[a-z]/
          return lowercaseRegex.test(val)
        },
        upperCase: (val) => {
          const uppercaseRegex = /[A-Z]/
          return uppercaseRegex.test(val)
        },
        number: (val) => {
          const numberRegex = /\d/
          return numberRegex.test(val)
        },
        specialCharacter: (val) => {
          const specialCharRegex = /[!@#$%^&*(),.?":{}|<>~`[\]\-_+=;'/\\]/
          return specialCharRegex.test(val)
        },
        minimumLength: (val) => {
          return val.length >= 8
        },
      },

      repassword: {
        required: requiredIf(function () {
          return this.$store.state.config.mode !== 'development' && this.form.isDriver
        }),
        minLength: minLength(8),
        maxLength: maxLength(50),
      },
    },
  },

  methods: {
    updatePassword(value) {
      this.$emit('update-form', { password: value })
    },
    updateRepassword(value) {
      this.$emit('update-form', { repassword: value })
    },
  },
}
</script>
